import { useState } from 'react';
import SEOMeta from '../../components/SEOMeta';
import { WHATSAPP_CTA_URL } from '../../data/whatsapp';

const C = {
  cream: 'var(--cream)',
  warmWhite: 'var(--warm-white)',
  earth: 'var(--earth)',
  earthLight: 'var(--earth-light)',
  gold: 'var(--gold)',
  goldLight: 'var(--gold-light)',
  goldPale: 'var(--gold-pale)',
  amberDeep: 'var(--amber-deep)',
  beigeMid: 'var(--beige-mid)',
  textBody: 'var(--text-body)',
  textMuted: 'var(--text-muted)',
  beige: 'var(--beige-footer)',
  terra: 'var(--terra)',
};

// --- Schema Markup ---

const productSchema = {
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: 'Música Personalizada — Presente Exclusivo',
  description:
    'Uma canção exclusiva criada com a história de quem você ama. Duas opções a partir de R$ 34,90, ambas prontas em até 30 minutos, 25 estilos musicais e refação sem custo.',
  brand: { '@type': 'Brand', name: 'Inspira Músicas' },
  offers: {
    '@type': 'AggregateOffer',
    lowPrice: '34.90',
    highPrice: '49.90',
    offerCount: '2',
    priceCurrency: 'BRL',
    availability: 'https://schema.org/InStock',
    deliveryLeadTime: { '@type': 'QuantitativeValue', value: '30', unitCode: 'MIN' },
  },
};

const pageFaqs = [
  {
    question: 'Preciso saber música ou tocar algum instrumento para encomendar?',
    answer:
      'Não, de jeito nenhum! Você só precisa contar a história da pessoa que quer homenagear — o que ela significa para você, memórias que vocês têm juntos, o jeito dela de ser. Nossa equipe faz todo o resto. Quanto mais detalhes você compartilhar, mais especial e emocionante vai ficar a música.',
  },
  {
    question: 'Posso ouvir a música antes de pagar?',
    answer:
      'Sim. Criamos uma prévia exclusiva para você ouvir antes de pagar. Você pode aprovar ou pedir uma alteração com o seu feedback; depois escolhe entre a música em áudio e a música com vídeo de fotos.',
  },
  {
    question: 'A música fica gravada e pode ser ouvida para sempre?',
    answer:
      'Sim! Você recebe um arquivo de áudio em MP3 diretamente no seu e-mail. Ele é seu para sempre — pode guardar no celular, compartilhar com a família, colocar no Instagram, fazer download no computador. A canção não some, não expira, não tem mensalidade. É um presente que fica.',
  },
  {
    question: 'Vocês entregam para qualquer cidade e estado do Brasil?',
    answer:
      'Sim, entregamos para todo o Brasil — e até para brasileiras que moram fora do país! Como a entrega é feita por e-mail, não importa onde a pessoa esteja: em São Paulo, no interior do Nordeste, em Portugal ou nos Estados Unidos. Se tiver um endereço de e-mail, a música chega.',
  },
  {
    question: 'Posso pedir mais de uma música? Existe desconto?',
    answer:
      'Claro! Muitas clientes pedem músicas para vários membros da família — uma para a mãe, outra para a avó, outra para a filha. Cada música é cobrada à parte, a partir de R$ 34,90, e criada de forma totalmente independente, com a história e o estilo de cada pessoa. Entre em contato conosco pelo WhatsApp para pedidos múltiplos e combinamos os detalhes.',
  },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: pageFaqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: { '@type': 'Answer', text: f.answer },
  })),
};

// --- Componentes internos ---

function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: `1px solid ${C.beigeMid}`, padding: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 0',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 16,
        }}
        aria-expanded={open}
      >
        <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 17, fontWeight: 500, color: C.earth, lineHeight: 1.4 }}>
          {question}
        </span>
        <span
          style={{
            flexShrink: 0,
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: open ? C.gold : C.goldPale,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background 0.2s',
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
          >
            <path d="M2 5l5 5 5-5" stroke={open ? '#fff' : C.gold} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      </button>
      {open && (
        <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: C.textBody, lineHeight: 1.7, paddingBottom: 20, margin: 0 }}>
          {answer}
        </p>
      )}
    </div>
  );
}

// --- Página principal ---

export default function MusicaPersonalizadaPage() {
  return (
    <>
      <SEOMeta
        title="Música Personalizada — Presente Exclusivo Entregue em 30 Minutos | Inspira Músicas"
        description="Crie uma música personalizada com a história de quem você ama. 25 estilos musicais, opções a partir de R$ 34,90 e entrega em até 30 min."
        path="/musica-personalizada"
        jsonLd={[productSchema, faqSchema]}
      />

      <div style={{ minHeight: '100vh', background: C.cream, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── Header ── */}
        <header
          style={{
            background: C.warmWhite,
            borderBottom: `1px solid ${C.beigeMid}`,
            padding: '16px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <a
            href="/"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 20, fontWeight: 700, color: C.earth, textDecoration: 'none' }}
          >
            Inspira Músicas
          </a>
          <a
            href={WHATSAPP_CTA_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: C.gold,
              color: '#fff',
              padding: '10px 20px',
              borderRadius: 100,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            Criar música
          </a>
        </header>

        {/* ── HERO ── */}
        <section
          style={{
            background: `linear-gradient(160deg, ${C.goldPale} 0%, ${C.cream} 100%)`,
            padding: '72px 24px 64px',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <p
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: C.gold,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                marginBottom: 16,
              }}
            >
              O presente que não tem preço
            </p>
            <h1
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(30px, 6vw, 52px)',
                fontWeight: 700,
                color: C.earth,
                margin: '0 0 20px',
                lineHeight: 1.2,
              }}
            >
              Música personalizada: o presente que fica para sempre
            </h1>
            <p
              style={{
                fontSize: 'clamp(16px, 2.5vw, 19px)',
                color: C.earthLight,
                maxWidth: 580,
                margin: '0 auto 36px',
                lineHeight: 1.7,
              }}
            >
              Imagine o rosto de quem você ama ao ouvir uma canção exclusiva, criada especialmente com a história de vocês dois. Esse presente não esquece, não estraga e não expira.
            </p>

            {/* CTAs */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 36 }}>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: C.gold,
                  color: '#fff',
                  padding: '16px 32px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                Criar minha música agora
              </a>
              <a
                href="/como-funciona"
                style={{
                  border: `1.5px solid ${C.earth}`,
                  color: C.earth,
                  padding: '16px 32px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                Ver como funciona
              </a>
            </div>

            {/* Selos */}
            <div
              style={{
                display: 'flex',
                gap: 8,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              {[
                { icon: '💰', text: 'A partir de R$ 34,90' },
                { icon: '⚡', text: 'Tudo em até 30 min' },
                { icon: '✅', text: 'Refação sem custo' },
              ].map((selo) => (
                <span
                  key={selo.text}
                  style={{
                    background: C.warmWhite,
                    border: `1px solid ${C.beigeMid}`,
                    borderRadius: 100,
                    padding: '8px 16px',
                    fontSize: 13,
                    color: C.textBody,
                    fontWeight: 500,
                  }}
                >
                  {selo.icon} {selo.text}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── O QUE É UMA MÚSICA PERSONALIZADA? ── */}
        <section style={{ padding: '72px 24px', background: C.warmWhite }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                marginBottom: 28,
                lineHeight: 1.25,
              }}
            >
              O que é uma música personalizada?
            </h2>
            <p style={{ fontSize: 16, color: C.textBody, lineHeight: 1.8, marginBottom: 20 }}>
              Uma <strong style={{ color: C.amberDeep }}>música personalizada</strong> é uma canção criada do zero, com a história, o nome e os sentimentos reais de uma pessoa específica. Não é uma música pronta com um nome colocado no meio — é uma homenagem musical verdadeira, onde a letra fala sobre quem a pessoa é, sobre memórias que vocês viveram juntos e sobre o que ela representa na sua vida.
            </p>
            <p style={{ fontSize: 16, color: C.textBody, lineHeight: 1.8, marginBottom: 20 }}>
              Pense numa <strong style={{ color: C.amberDeep }}>canção sob encomenda</strong> como uma carta transformada em melodia. Você conta a história, escolhe o estilo musical que mais combina com quem vai receber — sertanejo, gospel, MPB, pagode, entre mais de 25 opções — e nossa equipe compõe a música com base em tudo que você compartilhou. O resultado é uma peça única, que ninguém mais no mundo vai ter.
            </p>
            <p style={{ fontSize: 16, color: C.textBody, lineHeight: 1.8 }}>
              Uma <strong style={{ color: C.amberDeep }}>homenagem musical personalizada</strong> tem um poder que nenhum presente convencional consegue alcançar: ela toca o coração de um jeito que as palavras sozinhas não conseguem. É para isso que o Inspira Músicas existe — para transformar as histórias de amor das famílias brasileiras em canções exclusivas que ficam para sempre.
            </p>
          </div>
        </section>

        {/* ── COMO FUNCIONA ── */}
        <section style={{ padding: '72px 24px', background: C.cream }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                textAlign: 'center',
                marginBottom: 12,
                lineHeight: 1.25,
              }}
            >
              Como funciona?
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 48, lineHeight: 1.6 }}>
              Três passos simples. Do pedido à música na sua mão — tudo em 30 minutos.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                gap: 20,
              }}
            >
              {[
                {
                  num: '1',
                  icon: '💬',
                  title: 'Conte a história',
                  desc: 'Responda algumas perguntas sobre quem você quer homenagear: o jeito dela de ser, memórias especiais, o que ela significa para você. Quanto mais detalhes, mais especial fica a canção.',
                },
                {
                  num: '2',
                  icon: '🎵',
                  title: 'Escolha o estilo musical',
                  desc: 'Selecione entre mais de 25 gêneros — sertanejo, gospel, MPB, pagode, pop, forró e muito mais. Escolha também se quer voz masculina ou feminina. A música vai ter a cara de quem vai receber.',
                },
                {
                  num: '3',
                  icon: '📲',
                  title: 'Aprove e receba',
                  desc: 'Ouça uma prévia, aprove ou peça uma alteração e escolha entre a música em áudio ou a música com vídeo de fotos.',
                },
              ].map((step) => (
                <div
                  key={step.num}
                  style={{
                    background: C.warmWhite,
                    borderRadius: 16,
                    padding: '32px 28px',
                    border: `1px solid ${C.beigeMid}`,
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: C.goldPale,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: 18,
                      color: C.gold,
                      marginBottom: 16,
                    }}
                  >
                    {step.num}
                  </div>
                  <div style={{ fontSize: 32, marginBottom: 12 }}>{step.icon}</div>
                  <h3
                    style={{
                      fontFamily: "'Playfair Display', serif",
                      fontSize: 20,
                      fontWeight: 700,
                      color: C.earth,
                      marginBottom: 10,
                    }}
                  >
                    {step.title}
                  </h3>
                  <p style={{ fontSize: 15, color: C.textBody, lineHeight: 1.7, margin: 0 }}>{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── PARA QUAIS OCASIÕES? ── */}
        <section style={{ padding: '72px 24px', background: C.goldPale }}>
          <div style={{ maxWidth: 960, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                textAlign: 'center',
                marginBottom: 12,
                lineHeight: 1.25,
              }}
            >
              Para quais ocasiões?
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 48, lineHeight: 1.6 }}>
              Uma canção exclusiva é o presente certo para qualquer momento que merece ser lembrado para sempre.
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                gap: 16,
              }}
            >
              {[
                { icon: '💐', label: 'Dia das Mães', slug: 'dia-das-maes' },
                { icon: '🎂', label: 'Aniversário', slug: 'aniversario' },
                { icon: '💍', label: 'Casamento', slug: 'casamento' },
                { icon: '🎄', label: 'Natal', slug: 'natal' },
                { icon: '❤️', label: 'Dia dos Namorados', slug: 'dia-dos-namorados' },
                { icon: '🎓', label: 'Formatura', slug: 'formatura' },
                { icon: '👔', label: 'Dia dos Pais', slug: 'dia-dos-pais' },
                { icon: '🕊️', label: 'Homenagem póstuma', slug: 'homenagem' },
              ].map((item) => (
                <a
                  key={item.slug}
                  href={`/presente-${item.slug}`}
                  style={{
                    background: C.warmWhite,
                    border: `1px solid ${C.beigeMid}`,
                    borderRadius: 14,
                    padding: '24px 20px',
                    textAlign: 'center',
                    textDecoration: 'none',
                    display: 'block',
                    transition: 'box-shadow 0.2s',
                  }}
                >
                  <div style={{ fontSize: 32, marginBottom: 10 }}>{item.icon}</div>
                  <span
                    style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 15,
                      fontWeight: 500,
                      color: C.earth,
                    }}
                  >
                    {item.label}
                  </span>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* ── ESTILOS MUSICAIS ── */}
        <section style={{ padding: '72px 24px', background: C.cream }}>
          <div style={{ maxWidth: 860, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                textAlign: 'center',
                marginBottom: 12,
                lineHeight: 1.25,
              }}
            >
              25 estilos musicais disponíveis
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
              Do sertanejo ao gospel, do pagode ao jazz — a música personalizada vai soar como a cara de quem vai receber.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {[
                'Sertanejo', 'MPB', 'Gospel', 'Pop', 'Forró', 'Pagode',
                'Bossa Nova', 'Rock', 'Axé', 'Reggae', 'Jovem Guarda', 'Jazz',
                'Arrocha / Brega', 'Música Clássica', 'Infantil', 'Música Latina',
                'Rap', 'Black Music', 'Música Eletrônica', 'Bolero', 'Tango',
                'Valsa', 'Funk', 'Capoeira', 'Blues',
              ].map((style) => (
                <span
                  key={style}
                  style={{
                    background: C.goldPale,
                    border: `1px solid ${C.beigeMid}`,
                    borderRadius: 100,
                    padding: '8px 18px',
                    fontSize: 14,
                    fontWeight: 500,
                    color: C.amberDeep,
                  }}
                >
                  {style}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── QUANTO CUSTA? ── */}
        <section style={{ padding: '72px 24px', background: C.warmWhite }}>
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                textAlign: 'center',
                marginBottom: 12,
                lineHeight: 1.25,
              }}
            >
              Quanto custa uma música personalizada?
            </h2>
            <p style={{ textAlign: 'center', color: C.textMuted, fontSize: 16, marginBottom: 48, lineHeight: 1.6, maxWidth: 580, margin: '0 auto 48px' }}>
              Compare com outros presentes e veja por que uma canção exclusiva é a escolha mais emocionante pelo melhor custo-benefício.
            </p>

            {/* Tabela comparativa */}
            <div style={{ overflowX: 'auto', marginBottom: 40 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ background: C.beigeMid }}>
                    {['Presente', 'Preço médio', 'Dura quanto tempo?', 'Emoção'].map((h) => (
                      <th
                        key={h}
                        style={{
                          padding: '12px 16px',
                          textAlign: 'left',
                          fontFamily: "'DM Sans', sans-serif",
                          fontWeight: 600,
                          color: C.earth,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { nome: '💐 Buquê de flores', preco: 'R$ 80–150', duracao: '3–5 dias', emocao: 'Média' },
                    { nome: '🌸 Perfume', preco: 'R$ 100–300', duracao: '2–3 meses', emocao: 'Baixa' },
                    { nome: '🍫 Chocolate', preco: 'R$ 40–80', duracao: '1 dia', emocao: 'Baixa' },
                    { nome: '📸 Álbum de fotos', preco: 'R$ 50–150', duracao: 'Para sempre', emocao: 'Alta' },
                  ].map((row, i) => (
                    <tr key={row.nome} style={{ background: i % 2 === 0 ? C.cream : C.warmWhite }}>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.nome}</td>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.preco}</td>
                      <td style={{ padding: '12px 16px', color: C.textBody }}>{row.duracao}</td>
                      <td style={{ padding: '12px 16px', color: C.textMuted }}>{row.emocao}</td>
                    </tr>
                  ))}
                  <tr style={{ background: C.goldPale, fontWeight: 600 }}>
                    <td style={{ padding: '14px 16px', color: C.earth, fontWeight: 700 }}>🎵 Música exclusiva</td>
                    <td style={{ padding: '14px 16px', color: C.gold, fontWeight: 700 }}>A partir de R$ 34,90</td>
                    <td style={{ padding: '14px 16px', color: C.earth, fontWeight: 700 }}>Para sempre</td>
                    <td style={{ padding: '14px 16px', color: C.gold, fontWeight: 700 }}>Máxima ✨</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Destaque de preço */}
            <div
              style={{
                background: C.goldPale,
                border: `2px solid ${C.gold}`,
                borderRadius: 20,
                padding: '36px 32px',
                textAlign: 'center',
              }}
            >
              <p style={{ fontSize: 15, color: C.earthLight, marginBottom: 8 }}>A partir de</p>
              <div
                style={{
                  fontFamily: "'Playfair Display', serif",
                  fontSize: 'clamp(40px, 8vw, 64px)',
                  fontWeight: 700,
                  color: C.gold,
                  lineHeight: 1,
                  marginBottom: 8,
                }}
              >
                R$ 34,90
              </div>
              <p style={{ fontSize: 15, color: C.earthLight, marginBottom: 20 }}>
                Pagamento via PIX · Tudo pronto em até 30 minutos · Sem mensalidade
              </p>
              <p style={{ fontSize: 14, color: C.textMuted, marginBottom: 24 }}>
                🛡️ Garantia de 100% de satisfação — refazemos a música sem custo quando você pedir ajustes.
              </p>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: C.gold,
                  color: '#fff',
                  padding: '16px 40px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 16,
                  display: 'inline-block',
                }}
              >
                Criar minha música agora
              </a>
            </div>
          </div>
        </section>

        {/* ── PERGUNTAS FREQUENTES ── */}
        <section style={{ padding: '72px 24px', background: C.cream }}>
          <div style={{ maxWidth: 720, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(24px, 4vw, 36px)',
                fontWeight: 700,
                color: C.earth,
                marginBottom: 8,
                lineHeight: 1.25,
              }}
            >
              Perguntas frequentes
            </h2>
            <p style={{ color: C.textMuted, fontSize: 16, marginBottom: 40, lineHeight: 1.6 }}>
              Tudo que você precisa saber antes de criar a sua canção personalizada.
            </p>
            {pageFaqs.map((f, i) => (
              <FAQItem key={i} question={f.question} answer={f.answer} />
            ))}
            <p style={{ marginTop: 32, fontSize: 14, color: C.textMuted, lineHeight: 1.6 }}>
              Tem outra dúvida?{' '}
              <a href="/faq" style={{ color: C.gold, textDecoration: 'underline' }}>
                Veja todas as perguntas frequentes
              </a>{' '}
              ou fale com a gente pelo WhatsApp — respondemos na hora.
            </p>
          </div>
        </section>

        {/* ── CTA FINAL ── */}
        <section
          style={{
            padding: '80px 24px',
            background: `linear-gradient(160deg, ${C.goldPale} 0%, ${C.cream} 100%)`,
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 600, margin: '0 auto' }}>
            <h2
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(26px, 5vw, 40px)',
                fontWeight: 700,
                color: C.earth,
                marginBottom: 16,
                lineHeight: 1.2,
              }}
            >
              Crie a música de quem você ama
            </h2>
            <p style={{ fontSize: 17, color: C.earthLight, lineHeight: 1.7, marginBottom: 36 }}>
              Em 30 minutos, a pessoa mais especial da sua vida vai ouvir uma canção criada só para ela. Esse é o tipo de presente que faz chorar de emoção — do jeito bom.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href={WHATSAPP_CTA_URL}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: C.gold,
                  color: '#fff',
                  padding: '16px 36px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  fontWeight: 600,
                  fontSize: 16,
                }}
              >
                Criar minha música agora
              </a>
              <a
                href="/como-funciona"
                style={{
                  border: `1.5px solid ${C.earth}`,
                  color: C.earth,
                  padding: '16px 32px',
                  borderRadius: 100,
                  textDecoration: 'none',
                  fontWeight: 500,
                  fontSize: 16,
                }}
              >
                Ver como funciona
              </a>
            </div>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer
          style={{
            background: C.beige,
            padding: '24px',
            textAlign: 'center',
            fontSize: 13,
            color: C.textMuted,
          }}
        >
          © 2025 Inspira Músicas — AUTOMAT TECNOLOGIA DA INFORMACAO LTDA · CNPJ 59.640.197/0001-76
          <br />
          <a href="/privacidade" style={{ color: C.textMuted, marginRight: 12 }}>Privacidade</a>
          <a href="/termos" style={{ color: C.textMuted }}>Termos</a>
        </footer>

      </div>
    </>
  );
}
